import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui';
import styled, { css, keyframes } from 'styled-components';

import { Z_INDEX_MENU } from '@savgroup-front-common/constants/shared';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';

import { fontStyle } from '../../theme/helpers/fontStyle';

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
`;

const scaleOut = keyframes`
  from { opacity: 1; transform: scale(1); }
  to   { opacity: 0; transform: scale(0.95); }
`;

const contentStyles = css`
  background: ${({ theme }) => theme.systemColors.defaultBackground};
  border-radius: ${({ theme }) => theme.newUI.borders.mediumRadius};
  border: 1px solid ${({ theme }) => theme.colors.default};

  padding: ${spacing('sm')};
  min-width: ${spacing(44)};
  display: flex;
  flex-direction: column;
  gap: ${spacing('xxs')};
  z-index: ${Z_INDEX_MENU};
  transform-origin: var(--radix-dropdown-menu-content-transform-origin);

  &[data-state='open'] {
    animation: ${scaleIn} 150ms ease-out;
  }
  &[data-state='closed'] {
    animation: ${scaleOut} 100ms ease-in;
  }
`;

export const $DropdownMenuContent = styled(DropdownMenuPrimitive.Content)`
  ${contentStyles}
`;

export const $DropdownMenuSubContent = styled(DropdownMenuPrimitive.SubContent)`
  ${contentStyles}
`;

const itemMixin = css<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing('sm')};
  padding: ${spacing(1.5)} ${spacing('md')};
  border-radius: ${({ theme }) => theme.newUI.borders.smallRadius};
  ${fontStyle('sm')};
  font-weight: ${({ $isActive }) =>
    $isActive ? fontWeight('bold') : fontWeight('normal')};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary : theme.colors.mainTextColor};
  cursor: pointer;
  outline: none;
  user-select: none;
  background-color: transparent;

  svg {
    color: ${({ theme }) => theme.newUI.defaultColors.secondary};
    flex-shrink: 0;
  }

  &[data-highlighted],
  &:hover {
    background-color: ${({ theme }) => theme.newUI.variationColors.light7};
    color: ${({ theme }) => theme.colors.primary};
  }

  &[data-disabled] {
    color: ${({ theme }) => theme.colors.paragraphTextColor};
    pointer-events: none;
  }
`;

export const $DropdownMenuItem = styled(DropdownMenuPrimitive.Item)<{
  $isActive?: boolean;
}>`
  ${itemMixin};
`;

export const $DropdownMenuSubTrigger = styled(
  DropdownMenuPrimitive.SubTrigger,
)<{ $isActive?: boolean }>`
  ${itemMixin};
  justify-content: space-between;

  &[data-state='open'] {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const $DropdownMenuLabel = styled(DropdownMenuPrimitive.Label)`
  padding: ${spacing('xs')} ${spacing('sm')};
  ${fontStyle('lg')};
  font-weight: ${fontWeight('semibold')};
  color: ${({ theme }) => theme.colors.paragraphTextColor};
  text-transform: uppercase;
`;

export const $DropdownMenuSeparator = styled(DropdownMenuPrimitive.Separator)`
  height: ${spacing('px')};
  background: ${({ theme }) => theme.newUI.variationColors.light4};
  margin: ${spacing('xs')} 0;
`;
